/**
 * The landing and release strip (docs/ux/project-tab-spec.md §1.5 "Landing, build and release
 * strip"): one row for landing to the target branch, then one row per configured release lane
 * (`viko.project.yaml` `releaseLanes`). Landing attempts are collapsed per branch, so a branch
 * that failed three times and then landed shows as landed, never as four failures.
 */
import { landingModesNote } from './landing-modes.js'
import type { BuildCard, BuildLive, InboxItem, Landing, Machine, Project } from './types.js'
import { effectivePolicy } from './work.js'

export type LaneTrigger =
  | { kind: 'on-land' }
  | { kind: 'manual' }
  | { kind: 'debounce'; minutes: number }
  | { kind: 'nightly'; at: string }

/** A configured release lane, typed from the project config the web already holds. */
export interface ReleaseLaneInfo {
  id: string
  name: string
  target: string
  /** v1 machine key (`machines.allowed`), e.g. "mac". */
  machine?: string
  trigger: LaneTrigger
  production: boolean
}

/** A lane waiting for a tap or passkey (machine status `pendingShips` or a ship-tap card). */
export interface PendingShip {
  projectId: string
  laneId: string
  gate: string
  landings: number
  machineId?: string
}

export type ReleaseRowState = 'ok' | 'run' | 'wait' | 'fail' | 'idle'

export interface LandingFailureGroup {
  branch: string
  latest: Landing
  /** Earlier attempts for the same branch since it last landed (oldest last). */
  earlier: Landing[]
}

export interface ReleaseRow {
  /** `landing` or the lane id. */
  id: string
  /** Inspector key. */
  key: string
  kind: 'landing' | 'lane'
  name: string
  /** "auto on green", "manual", "on land · tap to ship", "passkey release"… */
  policy: string
  state: ReleaseRowState
  status: string
  at?: number
  /** Changes since the last build / waiting in the landing queue (always the full number). */
  count?: { n: number; label: string }
  note?: string
  action?: { verb: 'build'; label: string }
  laneId?: string
  production?: boolean
  gate?: string
  machineId?: string
  lane?: ReleaseLaneInfo
  /** Newest build first. */
  builds: BuildCard[]
  /** Lane: landings since its last build (newest first). Landing: in-flight queue. */
  landings: Landing[]
  /** Landing row: the last landed change. */
  landed?: Landing
  /** Landing row: branches whose newest attempt failed. */
  failures: LandingFailureGroup[]
  ship?: PendingShip
  /** Lane: what is live now (newest build with a verified/available live record). */
  live?: LaneLive
}

/** "What's live" for a lane/environment: the proof, the build and the tasks it shipped. */
export interface LaneLive {
  info: BuildLive
  build: BuildCard
  /** The landed change carrying the live commit, when known (opens in the inspector). */
  landing?: Landing
  /** A newer build is in flight or failed: live is still the older one. */
  newer?: BuildCard
}

/** The newest build whose live record says what this lane serves. */
export function laneLive(
  builds: readonly BuildCard[],
  landed: readonly Landing[],
): LaneLive | undefined {
  const idx = builds.findIndex((b) => b.live && String(b.status) === 'available')
  const build = builds[idx]
  if (!build?.live) return undefined
  const commit = build.live.commit
  const landing = landed.find(
    (l) => !!l.commit && (l.commit.startsWith(commit) || commit.startsWith(l.commit)),
  )
  return {
    info: build.live,
    build,
    ...(landing ? { landing } : {}),
    ...(idx > 0 && builds[0] ? { newer: builds[0] } : {}),
  }
}

/** "Live 0.3.1 · abc1234" — the compact label on the strip row (numbers never truncated). */
export function liveLabel(live: LaneLive): string {
  const sha = live.info.commit.slice(0, 7)
  return live.info.version === sha || live.info.version.startsWith(sha)
    ? `Live ${sha}`
    : `Live ${live.info.version} · ${sha}`
}

const DAY = 24 * 3_600_000

export function parseTrigger(raw: unknown): LaneTrigger {
  if (raw === undefined || raw === null) return { kind: 'on-land' }
  if (typeof raw === 'string') {
    if (raw === 'on-land') return { kind: 'on-land' }
    if (raw === 'nightly') return { kind: 'nightly', at: '03:00' }
    return { kind: 'manual' }
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    if (typeof o.debounce === 'number') return { kind: 'debounce', minutes: o.debounce }
    if (typeof o.nightly === 'string') return { kind: 'nightly', at: o.nightly }
    if (o.kind === 'on-land') return { kind: 'on-land' }
    if (o.kind === 'debounce' && typeof o.minutes === 'number')
      return { kind: 'debounce', minutes: o.minutes }
    if (o.kind === 'nightly')
      return { kind: 'nightly', at: typeof o.at === 'string' ? o.at : '03:00' }
  }
  return { kind: 'manual' }
}

/**
 * Release lanes from a project config: `releaseLanes` (parsed or raw YAML shape), or the legacy
 * `release.lanes`. Entries without an id are skipped.
 */
export function releaseLanesOf(config: unknown): ReleaseLaneInfo[] {
  if (!config || typeof config !== 'object') return []
  const c = config as { releaseLanes?: unknown; release?: { lanes?: unknown } }
  const list = Array.isArray(c.releaseLanes)
    ? c.releaseLanes
    : Array.isArray(c.release?.lanes)
      ? c.release.lanes
      : []
  return list.flatMap((raw): ReleaseLaneInfo[] => {
    if (!raw || typeof raw !== 'object') return []
    const l = raw as Record<string, unknown>
    if (typeof l.id !== 'string' || !l.id) return []
    return [
      {
        id: l.id,
        name: typeof l.name === 'string' && l.name ? l.name : l.id,
        target: typeof l.target === 'string' ? l.target : 'custom',
        ...(typeof l.machine === 'string' ? { machine: l.machine } : {}),
        trigger: parseTrigger(l.trigger),
        production: l.production === true,
      },
    ]
  })
}

export function triggerLabel(t: LaneTrigger, shipAuto: boolean, production: boolean): string {
  const when =
    t.kind === 'on-land'
      ? 'on land'
      : t.kind === 'manual'
        ? 'manual'
        : t.kind === 'debounce'
          ? `batched ${t.minutes} min after a landing`
          : `nightly at ${t.at}`
  if (production) return `${when} · passkey release`
  if (t.kind === 'manual') return when
  return shipAuto ? `auto ${when}` : `${when} · tap to ship`
}

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const short = (sha?: string | null) => (sha ? sha.slice(0, 7) : '')
const buildAt = (b: BuildCard) => b.updatedAt ?? b.createdAt ?? 0
const INFLIGHT = new Set(['queued', 'running', 'awaiting_approval'])
const RUNNING_BUILD = new Set(['queued', 'building', 'uploading', 'processing'])

/** Landing attempts collapsed per branch: only a branch whose newest attempt failed counts. */
export function landingFailures(landings: readonly Landing[], now: number): LandingFailureGroup[] {
  const byBranch = new Map<string, Landing[]>()
  for (const l of [...landings].sort((a, b) => b.updatedAt - a.updatedAt)) {
    const k = l.branch ?? l.workstreamId ?? l.id
    byBranch.set(k, [...(byBranch.get(k) ?? []), l])
  }
  const out: LandingFailureGroup[] = []
  for (const [branch, list] of byBranch) {
    const latest = list[0]
    if (!latest || latest.state !== 'failed' || now - latest.updatedAt > DAY) continue
    const sinceLanded = list.slice(1)
    const stop = sinceLanded.findIndex((l) => l.state === 'landed')
    const earlier = (stop < 0 ? sinceLanded : sinceLanded.slice(0, stop)).filter(
      (l) => !INFLIGHT.has(l.state),
    )
    out.push({ branch, latest, earlier })
  }
  return out
}

function landingRow(landings: Landing[], autoLand: boolean, now: number): ReleaseRow {
  const row = landingRowBase(landings, autoLand, now)
  // landing-modes: target branch, tier and conflict wait of the newest landing that has them.
  const featured = [...landings]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map((l) => landingModesNote(l))
    .find(Boolean)
  return featured ? { ...row, note: row.note ? `${row.note} · ${featured}` : featured } : row
}

function landingRowBase(landings: Landing[], autoLand: boolean, now: number): ReleaseRow {
  const sorted = [...landings].sort((a, b) => b.updatedAt - a.updatedAt)
  const inflight = sorted.filter((l) => INFLIGHT.has(l.state)).reverse()
  const running = inflight.find((l) => l.state === 'running')
  const awaiting = inflight.find((l) => l.state === 'awaiting_approval')
  const landed = sorted.find((l) => l.state === 'landed')
  const failures = landingFailures(sorted, now)
  const base = {
    id: 'landing',
    key: 'release:landing',
    kind: 'landing' as const,
    name: 'Landing to main',
    policy: autoLand ? 'auto on green' : 'tap to land',
    builds: [],
    landings: inflight,
    failures,
    ...(landed ? { landed } : {}),
  }
  const name = (l: Landing) => l.branch ?? l.summary ?? 'a branch'
  const waiting = inflight.filter((l) => l.state === 'queued' && l !== running)
  const queue = waiting.length
    ? { count: { n: waiting.length, label: `${waiting.length} queued` } }
    : {}
  if (running)
    return {
      ...base,
      state: 'run',
      status: `Landing ${name(running)} · ${running.step ?? 'starting'}`,
      at: running.updatedAt,
      ...queue,
    }
  if (awaiting)
    return {
      ...base,
      state: 'wait',
      status: `${name(awaiting)} waits for you to approve`,
      at: awaiting.updatedAt,
      ...queue,
    }
  if (waiting[0])
    return {
      ...base,
      state: 'run',
      status: `Queued: ${name(waiting[0])}`,
      at: waiting[0].updatedAt,
      ...queue,
    }
  const fail = failures[0]
  if (fail) {
    const why = fail.latest.reason ?? fail.latest.failure?.message ?? fail.latest.error ?? 'failed'
    const more =
      failures.length > 1
        ? ` · ${plural(failures.length - 1, 'other branch', 'other branches')} failing`
        : ''
    return {
      ...base,
      state: 'fail',
      status: `${fail.branch}: ${why}`,
      at: fail.latest.updatedAt,
      ...(fail.earlier.length || more
        ? {
            note: `${fail.earlier.length ? plural(fail.earlier.length, 'earlier attempt') : ''}${fail.earlier.length ? more : more.replace(/^ · /, '')}`,
          }
        : {}),
    }
  }
  if (landed)
    return {
      ...base,
      state: 'ok',
      status: `Landed ${short(landed.commit) || name(landed)}`,
      at: landed.updatedAt,
    }
  return { ...base, state: 'idle', status: 'Nothing landed yet' }
}

function buildLabel(b: BuildCard): string {
  return b.version ? `Build ${b.version}` : b.title
}

function laneRow(
  lane: ReleaseLaneInfo,
  builds: BuildCard[],
  landed: Landing[],
  ship: PendingShip | undefined,
  shipAuto: boolean,
  machineId: string | undefined,
): ReleaseRow {
  const latest = builds[0]
  const live = laneLive(builds, landed)
  const since = latest
    ? landed.filter((l) => l.updatedAt > (latest.createdAt ?? buildAt(latest)))
    : landed
  const n = ship ? Math.max(ship.landings, since.length) : since.length
  const base = {
    id: lane.id,
    key: `release:${encodeURIComponent(lane.id)}`,
    kind: 'lane' as const,
    name: lane.name,
    policy: triggerLabel(lane.trigger, shipAuto, lane.production),
    laneId: lane.id,
    production: lane.production,
    lane,
    builds,
    landings: since,
    failures: [],
    ...(machineId ? { machineId } : {}),
    ...(ship ? { ship, gate: ship.gate } : {}),
    ...(live ? { live } : {}),
  }
  const count = n
    ? { count: { n, label: `${plural(n, 'change')} ${latest && !ship ? 'since' : 'ready'}` } }
    : {}
  const build = lane.production ? 'Release with passkey' : 'Build now'
  const action = (label = build) => ({ action: { verb: 'build' as const, label } })
  if (ship)
    return {
      ...base,
      state: 'wait',
      status: ship.gate === 'step-up' ? 'Waits for a passkey tap' : 'Waits for your tap',
      ...(latest ? { at: buildAt(latest) } : {}),
      ...count,
      ...action(lane.production ? build : `Build ${plural(n || 1, 'change')}`),
    }
  if (!latest)
    return {
      ...base,
      state: 'idle',
      status: 'No build yet',
      ...count,
      ...action(),
    }
  const at = buildAt(latest)
  const host = latest.links[0]?.url.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
  if (latest.status === 'failed') {
    const cause = latest.failure?.cause ?? latest.detail ?? 'failed'
    return {
      ...base,
      state: 'fail',
      status: `${buildLabel(latest)} failed: ${cause}`,
      at,
      ...(latest.failure?.fix ? { note: latest.failure.fix } : {}),
      ...count,
      ...action(lane.production ? build : 'Retry build'),
    }
  }
  if (RUNNING_BUILD.has(String(latest.status)))
    return {
      ...base,
      state: latest.production && latest.status === 'queued' ? 'wait' : 'run',
      status:
        latest.production && latest.status === 'queued'
          ? 'Waits for a passkey tap'
          : `${buildLabel(latest)} · ${latest.status}`,
      at,
      ...count,
      ...(latest.production && latest.status === 'queued' ? action() : {}),
    }
  return {
    ...base,
    state: 'ok',
    status: `${buildLabel(latest)}${host ? ` on ${host}` : ''}`,
    at,
    ...count,
    ...(n && (lane.trigger.kind === 'manual' || lane.production) ? action() : {}),
  }
}

/** Which enrolled machine a lane builds on (its v1 key), else the ship card's, else the first online one. */
function laneMachine(
  lane: ReleaseLaneInfo,
  project: Pick<Project, 'machines' | 'config'>,
  machines: readonly Machine[],
  ship?: PendingShip,
): string | undefined {
  if (ship?.machineId) return ship.machineId
  const own = machines.filter((m) => project.machines.includes(m.id))
  if (lane.machine) {
    const hit = own.find(
      (m) =>
        m.configKey === lane.machine ||
        m.slug === lane.machine ||
        m.id === lane.machine ||
        m.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') === lane.machine,
    )
    if (hit) return hit.id
  }
  return (own.find((m) => m.online) ?? own[0])?.id
}

export interface ReleaseStripInput {
  project: Pick<Project, 'id' | 'loop' | 'config' | 'machines'>
  landings: readonly Landing[]
  builds: readonly BuildCard[]
  machines: readonly Machine[]
  inbox?: readonly InboxItem[]
  autoLand: boolean
  now: number
}

/** Ship taps for this project: machine status (encrypted accounts) and ship-tap cards (hub). */
export function pendingShipsFor(input: ReleaseStripInput): PendingShip[] {
  const pid = input.project.id
  const out: PendingShip[] = []
  for (const m of input.machines)
    for (const s of m.pendingShips ?? [])
      if (s.projectId === pid) out.push({ ...s, machineId: s.machineId ?? m.id })
  for (const i of input.inbox ?? []) {
    if (i.category !== 'ship-tap') continue
    const b = (i.body ?? {}) as Partial<PendingShip>
    if (b.projectId !== pid || !b.laneId || out.some((s) => s.laneId === b.laneId)) continue
    out.push({
      projectId: pid,
      laneId: b.laneId,
      gate: b.gate ?? 'tap',
      landings: b.landings ?? 0,
      ...(b.machineId ? { machineId: b.machineId } : {}),
    })
  }
  return out
}

export function releaseStrip(input: ReleaseStripInput): ReleaseRow[] {
  const { project, now } = input
  const pid = project.id
  const landings = input.landings.filter((l) => l.projectId === pid)
  const landed = landings
    .filter((l) => l.state === 'landed')
    .sort((a, b) => b.updatedAt - a.updatedAt)
  const builds = input.builds
    .filter((b) => b.projectId === pid)
    .sort((a, b) => buildAt(b) - buildAt(a))
  const ships = pendingShipsFor(input)
  const shipAuto =
    effectivePolicy(
      {
        loopProfile: project.config?.loopProfile ?? project.loop,
        ...(project.config?.autonomy ? { autonomy: project.config.autonomy } : {}),
      },
      'ship-internal',
    ) === 'auto'
  const lanes = releaseLanesOf(project.config)
  // Builds of lanes no longer (or not yet) in the config still get a row, never dropped.
  for (const b of builds)
    if (!lanes.some((l) => l.id === b.laneId))
      lanes.push({
        id: b.laneId,
        name: b.title.replace(/^Build \S+ on /, '') || b.laneId,
        target: 'custom',
        trigger: { kind: 'manual' },
        production: b.production,
      })
  const rows = [landingRow(landings, input.autoLand, now)]
  for (const lane of lanes) {
    const ship = ships.find((s) => s.laneId === lane.id)
    rows.push(
      laneRow(
        lane,
        builds.filter((b) => b.laneId === lane.id),
        landed,
        ship,
        shipAuto,
        laneMachine(lane, project, input.machines, ship),
      ),
    )
  }
  return rows
}
